import type { Registry as Services } from '@ember/service';
import type { Dayjs } from 'dayjs';
import { tKey } from 'ember-intl';
import type { GeneralDatepickersDatepickerWithSidebarSignature } from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import {
  GROUP_BY,
  type GroupBy,
} from 'lumen/utils/constants/market-snapshots-constants';

// Also the smallest option, which sets the footer threshold, not the user's
// selected page size. A user on 100 rows with 51–100 properties still sees the
// footer, so they can switch back to 50.
export const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [50, 100];

const DATE_FORMAT = 'YYYY-MM-DD';

/** Current month, used when the URL has no `fromDate` / `toDate`. */
export function getDefaultDateRange(today: Dayjs): {
  fromDate: string;
  toDate: string;
} {
  return {
    fromDate: today.startOf('month').format(DATE_FORMAT),
    toDate: today.endOf('month').format(DATE_FORMAT),
  };
}

/** Datepicker presets. */
export function buildSnapshotSidebarLists(
  today: Dayjs,
  intl: Services['intl'],
  groupBy: GroupBy,
): GeneralDatepickersDatepickerWithSidebarSignature['Args']['sidebarLists'] {
  const lastYear = {
    id: 'last-year',
    text: tKey('general.last_year'),
    range: {
      start: today.subtract(1, 'year').startOf('year'),
      end: today.subtract(1, 'year').endOf('year'),
    },
  };
  const thisYear = {
    id: 'this-year',
    text: tKey('general.this_year'),
    range: { start: today.startOf('year'), end: today.endOf('year') },
  };
  const nextYear = {
    id: 'next-year',
    text: tKey('general.next_year'),
    range: {
      start: today.add(1, 'year').startOf('year'),
      end: today.add(1, 'year').endOf('year'),
    },
  };
  const custom = {
    id: 'custom',
    text: tKey('general.custom'),
    range: { start: today.startOf('month'), end: today.endOf('month') },
  };

  const options =
    groupBy === GROUP_BY.MONTH
      ? [lastYear, thisYear, nextYear, custom]
      : [
          {
            id: 'today',
            text: tKey('general.today'),
            range: { start: today, end: today },
          },
          {
            id: 'this-month',
            text: tKey('general.this_month'),
            range: { start: today.startOf('month'), end: today.endOf('month') },
          },
          {
            id: 'next-90-days',
            text: tKey('general.next_days'),
            count: 90,
            range: { start: today, end: today.add(90, 'day') },
          },
          lastYear,
          thisYear,
          nextYear,
          custom,
        ];

  return [{ title: intl.t(tKey('general.selected_period')), options }];
}
